import type { ErrorCode, ErrorResponse as GeneratedErrorResponse } from "@/lib/generated/types.gen";

const DEFAULT_ERROR_MESSAGE = "Request failed";
const STATUS_TEXT_FALLBACKS: Record<number, string> = {
  400: "Bad Request",
  401: "Unauthorized",
  402: "Payment Required",
  403: "Forbidden",
  404: "Not Found",
  405: "Method Not Allowed",
  409: "Conflict",
  422: "Unprocessable Entity",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

export const DEFAULT_RETRY_AFTER_SECONDS = 60;

export interface ApiErrorMetadata {
  status: number;
  statusText: string;
  url?: string;
  errorCode?: ErrorCode;
  requestId?: string;
  retryAfter?: number;
  details?: Record<string, unknown> | null;
}

export type ApiError = Error & { readonly metadata: ApiErrorMetadata };

type ResponseLike = Pick<Response, "status" | "statusText" | "headers"> & {
  url?: string;
};

interface NormalizedPayload {
  message?: string;
  status?: number;
  errorCode?: ErrorCode;
  requestId?: string;
  details?: Record<string, unknown> | null;
}

interface CreateErrorFromResponseOptions {
  url?: string;
  fallbackMessage?: string;
  retryAfterSeconds?: number;
}

export function createApiError(message: string, metadata: ApiErrorMetadata): ApiError {
  const normalizedMetadata: ApiErrorMetadata = {
    ...metadata,
    details: metadata.details ?? null,
  };

  const error = new Error(message) as ApiError;
  Object.defineProperty(error, "metadata", {
    value: Object.freeze(normalizedMetadata),
    enumerable: false,
    writable: false,
    configurable: false,
  });
  error.name = `ApiError[${normalizedMetadata.status}]`;
  return error;
}

export function createErrorFromResponse(
  response: ResponseLike,
  errorPayload: unknown,
  options: CreateErrorFromResponseOptions = {},
): ApiError {
  const normalizedPayload = normalizeErrorPayload(errorPayload);
  const payloadStatus = normalizedPayload.status;
  const status =
    typeof payloadStatus === "number" && !Number.isNaN(payloadStatus)
      ? payloadStatus
      : response.status || 500;
  const statusText = resolveStatusText(response, status);
  const fallbackMessage = options.fallbackMessage ?? statusText;
  const payloadMessage = normalizedPayload.message?.trim();
  const message = payloadMessage && payloadMessage.length > 0 ? payloadMessage : fallbackMessage;

  return createApiError(message, {
    status,
    statusText,
    url: resolveUrl(options.url, response.url),
    errorCode: normalizedPayload.errorCode,
    requestId: normalizedPayload.requestId,
    retryAfter: options.retryAfterSeconds,
    details: normalizedPayload.details ?? null,
  });
}

export function getRetryAfterSeconds(headers: Headers): number | undefined {
  const value = headers.get("retry-after");
  if (!value) {
    return undefined;
  }

  const numeric = Number(value);
  if (!Number.isNaN(numeric) && numeric >= 0) {
    return numeric;
  }

  const parsedDate = Date.parse(value);
  if (Number.isNaN(parsedDate)) {
    return undefined;
  }

  const diffSeconds = Math.max(0, Math.round((parsedDate - Date.now()) / 1000));
  return diffSeconds;
}

export function isApiError(error: unknown): error is ApiError {
  if (!isObject(error)) {
    return false;
  }
  if (!("metadata" in error)) {
    return false;
  }
  const candidate = (error as { metadata?: unknown }).metadata;
  return isApiErrorMetadata(candidate);
}

export function isAuthenticationError(error: unknown): error is ApiError {
  return isStatusError(error, 401);
}

export function isAccountInactiveError(error: unknown): error is ApiError {
  return isStatusError(error, 403) && error.metadata.errorCode === "account_inactive";
}

// Mutually exclusive with isAccountInactiveError: inactive-account 403s are an
// auth-state condition (hard redirect to /account-inactive), not a permission
// failure. Callers that want every 403 must check both guards.
export function isPermissionError(error: unknown): error is ApiError {
  return isStatusError(error, 403) && error.metadata.errorCode !== "account_inactive";
}

export function isNotFoundError(error: unknown): error is ApiError {
  return isStatusError(error, 404);
}

export function isRateLimitError(error: unknown): error is ApiError {
  return isStatusError(error, 429);
}

export function isConflictError(error: unknown): error is ApiError {
  return isStatusError(error, 409);
}

function isStatusError(error: unknown, status: number): error is ApiError {
  if (!isApiError(error)) {
    return false;
  }
  return error.metadata.status === status;
}

function normalizeErrorPayload(payload: unknown): NormalizedPayload {
  if (typeof payload === "string") {
    return { message: payload };
  }

  if (!isObject(payload)) {
    return {};
  }

  const generated = payload as Partial<GeneratedErrorResponse>;
  const legacy = payload as {
    error_code?: unknown;
    details?: unknown;
    requestId?: unknown;
    status?: unknown;
  };
  const normalized: NormalizedPayload = {};

  if (typeof generated.message === "string") {
    normalized.message = generated.message;
  }
  if (typeof generated.status === "number") {
    normalized.status = generated.status;
  } else if (typeof legacy.status === "number") {
    normalized.status = legacy.status;
  }
  if (typeof generated.request_id === "string") {
    normalized.requestId = generated.request_id;
  } else if (typeof legacy.requestId === "string") {
    normalized.requestId = legacy.requestId;
  }
  if (typeof generated.code === "string") {
    normalized.errorCode = generated.code as ErrorCode;
  } else if (typeof legacy.error_code === "string") {
    normalized.errorCode = legacy.error_code as ErrorCode;
  }

  const detailsCandidate = "details" in generated ? generated.details : legacy.details;
  if (detailsCandidate === null) {
    normalized.details = null;
  } else if (isRecord(detailsCandidate)) {
    normalized.details = detailsCandidate;
  }

  return normalized;
}

function resolveStatusText(response: ResponseLike, status: number): string {
  if (response.statusText && response.statusText.trim().length) {
    return response.statusText;
  }
  return STATUS_TEXT_FALLBACKS[status] ?? DEFAULT_ERROR_MESSAGE;
}

function resolveUrl(preferred?: string, responseUrl?: string): string | undefined {
  if (preferred && preferred.trim().length) {
    return preferred;
  }
  if (responseUrl && responseUrl.trim().length) {
    return responseUrl;
  }
  return undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isObject(value);
}

function isApiErrorMetadata(value: unknown): value is ApiErrorMetadata {
  if (!isRecord(value)) {
    return false;
  }
  const status = value["status"];
  if (typeof status !== "number") {
    return false;
  }
  const statusText = value["statusText"];
  if (typeof statusText !== "string") {
    return false;
  }
  if ("details" in value) {
    const details = value["details"];
    if (details !== null && !isRecord(details)) {
      return false;
    }
  }
  return true;
}
